import { Film, Languages, Globe2, Clapperboard } from "lucide-react";
import { ASSETS } from "@/lib/site";

const PILLARS = [
  {
    icon: Languages,
    title: "Native lip‑sync, six languages",
    text: "Kannada, English, Hindi, Telugu, Tamil and Malayalam — shot together, not dubbed apart.",
  },
  {
    icon: Globe2,
    title: "South to Pan‑India to Asia",
    text: "Built in Bangalore to travel — stories engineered for theatrical and OTT across regions.",
  },
  {
    icon: Clapperboard,
    title: "Director‑Producer‑Actor",
    text: "Nagaveni Santhosha leads every department with a 12‑year Bollywood pedigree.",
  },
  {
    icon: Film,
    title: "Cinema‑first craft",
    text: "Arri cinema cameras, professional gimbal rigs, location‑first cinematography.",
  },
];

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative scroll-mt-nav py-28 md:py-36 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.3em] uppercase text-[#2DD4BF] mb-6">01 · The House</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-white">
              A production house from <span className="italic text-gradient-aum">Bangalore</span>,
              made for the world.
            </h2>
            <div className="divider-aum my-8" />
            <p className="text-slate-300 text-base md:text-lg leading-relaxed">
              AU Movies was founded on a single, audacious idea — that a film from South India can
              be shot in six languages at once, with English lip‑sync that doesn't compromise the
              actor's craft. We are the first to attempt it, and we're building a slate that proves it.
            </p>
            <p className="text-slate-400 mt-4 leading-relaxed">
              Every frame is shot on cinema‑grade equipment with a director who has spent twelve
              years inside seven Bollywood productions before launching AU Movies as Producer,
              Director and Lead Actor of his own stories.
            </p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-12 grid-rows-6 gap-4 h-[640px]">
            <div
              className="col-span-7 row-span-4 rounded-2xl bg-cover bg-center relative overflow-hidden hover-lift border border-white/5"
              style={{ backgroundImage: `url(${ASSETS.gimbal})` }}
              data-testid="about-bento-gimbal"
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <p className="text-xs tracking-[0.25em] uppercase text-[#2DD4BF]">On Set</p>
                <p className="font-serif text-2xl text-white mt-1">Cinema‑grade Gimbal Cinematography</p>
              </div>
            </div>

            <div className="col-span-5 row-span-3 rounded-2xl bg-cover bg-center relative overflow-hidden hover-lift border border-white/5"
                 style={{ backgroundImage: `url(${ASSETS.arri})` }}
                 data-testid="about-bento-arri">
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-5">
                <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37]">Equipment</p>
                <p className="font-serif text-xl text-white mt-1">Arri Cinema Camera</p>
              </div>
            </div>

            <div className="col-span-5 row-span-3 rounded-2xl glass-card relative overflow-hidden p-6 hover-lift" data-testid="about-bento-stat">
              <p className="text-xs tracking-[0.25em] uppercase text-[#2DD4BF]">Languages Per Production</p>
              <p className="font-serif text-6xl text-gradient-gold mt-2 leading-none">6</p>
              <p className="text-slate-400 text-sm mt-4 leading-relaxed">
                Kannada · English · Hindi<br />Telugu · Tamil · Malayalam
              </p>
            </div>

            <div className="col-span-7 row-span-2 rounded-2xl bg-cover bg-center relative overflow-hidden hover-lift border border-white/5"
                 style={{ backgroundImage: `url(${ASSETS.earthMoon})` }}
                 data-testid="about-bento-earth">
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-5">
                <p className="text-xs tracking-[0.25em] uppercase text-[#2DD4BF]">Reach</p>
                <p className="font-serif text-xl text-white mt-1">Pan‑India · Asia</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PILLARS.map(({ icon: Icon, title, text }) => (
            <div
              key={title}
              data-testid={`pillar-${title.split(" ")[0].toLowerCase()}`}
              className="glass-card rounded-xl p-6 hover-lift"
            >
              <Icon size={22} strokeWidth={1.5} className="text-[#2DD4BF]" />
              <h3 className="font-serif text-xl text-white mt-4">{title}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
